import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useChatStore } from 'src/store/chat';
import { useMutation } from '@tanstack/react-query';
import { ArrowUp, Loader2 } from 'lucide-react';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { WelcomeService } from 'src/services/welcome.service';
import { useAI } from 'src/hooks/useAI';
import { useLocation } from 'react-router-dom';
import { useAIStore } from 'src/store/ai';
import { toast } from 'react-toastify';

import { cn } from '@utils/index';

interface SidebarInputProps {
  onSend?: (message: string) => void;
  inProgress?: boolean;
}

export const SidebarInput = ({ onSend, inProgress = false }: SidebarInputProps) => {
  const location = useLocation();
  const isThanks = location.pathname === '/thanks';
  const setMessages = useChatStore((state) => state.setMessages);
  const setSessionId = useChatStore((state) => state.setSessionId);
  const setSuggestedQuestions = useChatStore(
    (state) => state.setSuggestedQuestions
  );
  const [showFade, setShowFade] = useState(false);
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const [text, setText] = useState('');
  const { sendMessage: sendInsuranceMessage } = useAI();
  
  // Get loading state and error from AI store
  const { chatLoading, chatError, clearErrors } = useAIStore();

  // Show toast on chat error
  useEffect(() => {
    if (chatError) {
      toast.error(chatError, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      clearErrors();
    }
  }, [chatError, clearErrors]);
  
  // Allow input if actively loading/processing
  const isInputDisabled = useMemo(() => {
    if (isThanks) return true;
    if (inProgress || chatLoading) return true;
    return false;
  }, [inProgress, chatLoading, isThanks]);

  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: async (message: string) => {
      try {
        console.log('[SidebarInput] mutationFn start');
        await sendInsuranceMessage(message);
        console.log('[SidebarInput] mutationFn done');
      } catch (err) {
        console.error('[SidebarInput] mutationFn error:', err);
        throw err;
      }
    },
    onError: (error) => {
      console.error('[SidebarInput] Mutation onError:', error);
    },
    onSuccess: () => {
      console.log('[SidebarInput] Mutation onSuccess');
    },
  });

  const { mutateAsync: getVideoAgent } = useMutation({
    mutationFn: WelcomeService.getWelcomeVideoAgent,
    onSuccess: async (data) => {
      const { session_id, suggested_questions } = data;
      setSessionId(session_id);
      setSuggestedQuestions(suggested_questions);
    },
    onError: (error) => {
      console.error('Video agent failed:', error);
    },
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTextareaHeight = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.minHeight = '30px';
    textareaRef.current.style.overflowY = 'hidden';
  };


  const handleSubmit = async (value: string) => {
    console.log('[SidebarInput] handleSubmit', value);
    try {
      // Call onSend callback if provided
      if (onSend) {
        console.log('[SidebarInput] Calling onSend callback');
        onSend(value);
      }
      // Send to our backend
      console.log('[SidebarInput] Calling sendMessage mutation');
      await sendMessage(value);
      console.log('[SidebarInput] sendMessage completed successfully');
    } catch (error) {
      console.error('[SidebarInput] handleSubmit error:', error);
      throw error;
    }

    // Restore focus to textarea after submitting
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleSend = async () => {
    const message = text.trim();
    console.log('[SidebarInput] handleSend', message);
    if (message === '') {
      console.log('[SidebarInput] Empty message, returning');
      return;
    }
    setText('');
    resetTextareaHeight();
    try {
      console.log('[SidebarInput] Calling handleSubmit');
      await handleSubmit(message);
      console.log('[SidebarInput] handleSubmit completed');
    } catch (error) {
      console.error('[SidebarInput] handleSend error:', error);
    }
    setShowFade(false);

    // Restore focus to textarea after sending message
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  const wrapperStyle = 'flex gap-2 p-4 absolute w-full';
  const inputStyle =
    'placeholder:text-base 2xl:placeholder:text-base w-full placeholder:font-inherit placeholder:text-placeholder outline-none';
  const buttonStyle =
    'flex items-center justify-center w-[34px] h-[34px] bg-primary rounded-full p-2 text-white disabled:bg-gray-400 disabled:cursor-not-allowed';

  return (
    <div
      className={cn(
        wrapperStyle,
        'sidebar-footer mt-auto sticky bottom-0 xl:rounded-br-4xl flex flex-col'
      )}
    >
      <div className="flex gap-2 items-center relative mt-auto">
        <div className="relative w-full">
          <textarea
            ref={textareaRef}
            disabled={isInputDisabled || isPending}
            placeholder="Ask me anything..."
            className={cn(
              inputStyle,
              'resize-none overflow-y-auto max-h-36 pr-12',
              isThanks && 'pointer-events-none!'
            )}
            rows={1}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
            onInput={(e) => {
              const target = e.currentTarget;

              target.style.height = 'auto';

              const lineHeight = parseFloat(
                getComputedStyle(target).lineHeight
              );
              const linesCount = Math.floor(target.scrollHeight / lineHeight);

              const maxLines = 3;
              const maxHeight = lineHeight * maxLines;

              if (linesCount > maxLines) {
                target.style.height = `${maxHeight}px`;
                target.style.overflowY = 'auto';
                setShowFade(true);
              } else {
                target.style.height = `${target.scrollHeight}px`;
                target.style.overflowY = 'hidden';
                setShowFade(false);
              }

              // Maintain focus after height adjustment
              target.focus();
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                await handleSend();
              }
            }}
          />

          {showFade && (
            <div
              className="pointer-events-none absolute top-0 left-0 right-0 h-6"
              style={{
                background: 'linear-gradient(to bottom, white, transparent)',
              }}
            />
          )}
        </div>

        <button
          disabled={isInputDisabled || isPending}
          className={cn(
            buttonStyle,
            'absolute right-0',
            text.trim().length > 0
              ? 'bottom-0 translate-y-0'
              : 'top-1/2 -translate-y-1/2',
            'cursor-pointer'
          )}
          onClick={handleSend}
        >
          {inProgress || isPending ? (
            <Loader2 className="text-white animate-spin" />
          ) : (
            <ArrowUp className="text-white" />
          )}
        </button>
      </div>
    </div>
  );
};
